import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { numberFormatLong } from "components/app_layout/Typography/Numbers";
import CreditUsageBar from "components/contacts/cognism/CreditUsageBar";
import SkeletonText from "components/ui/skeleton/SkeletonText";
import {
  ContactsCreditUsageHistory,
  useContactsCreditUsageHistory,
} from "lib/hooks/api/contacts/useContactsCreditUsageHistory";
import {
  ContactsCreditsReset,
  useContactsExportCredits,
} from "lib/hooks/api/contacts/useContactsExportCredits";
import { useCurrentUser } from "lib/hooks/api/users/useCurrentUser";
import { CreditResetCadence } from "lib/types/graphQLEnums";
import { pluralise } from "lib/utils";
import ContactCreditsSpendByUser from "./ContactCreditsSpendByUser";
import ContactCreditsUsageChart from "./ContactCreditsUsageChart";

const CADENCE_ADVERB: Record<CreditResetCadence, string> = {
  [CreditResetCadence.Monthly]: "every month",
  [CreditResetCadence.Quarterly]: "every quarter",
  [CreditResetCadence.Annual]: "every year",
};

export function CreditsAndUsage() {
  const { totalCredits, usedCredits, remainingCredits, reset, balanceKnown, isLoading } =
    useContactsExportCredits();
  const history = useContactsCreditUsageHistory();
  const { data: currentUser } = useCurrentUser();
  const isCompanyAdmin = !!currentUser?.isCompanyAdmin || !!currentUser?.isAdmin;

  const loadFailed = !isLoading && !balanceKnown;
  const resetMessage = resetCopy(reset);

  // No allowance and nothing left: an admin has switched credits off (an allocation of 0), as
  // opposed to a pool that has been used up.
  const notEnabled = balanceKnown && totalCredits === 0 && remainingCredits === 0;

  return (
    <Flex column gap={24}>
      <Flex column gap={4}>
        <Text h2>Credits &amp; usage</Text>
        <Description>
          Contact credits are shared across your company and are spent when an export or a bulk
          reveal uncovers a contact nobody on your team has revealed before. Revealing contacts one
          at a time, and exporting a contact you already own, is free.
        </Description>
      </Flex>

      {loadFailed && (
        <Card>
          <Text>
            We couldn&apos;t load your credit balance. Refresh the page, and get in touch if it
            keeps happening.
          </Text>
        </Card>
      )}

      {notEnabled && (
        <Card>
          <Text fontWeight="600">Contact credits are switched off for your company</Text>
          <Description>
            Exports can still include contacts your team has already revealed. Talk to your account
            manager to turn on credits for revealing new ones.
          </Description>
        </Card>
      )}

      {!notEnabled && !loadFailed && (
        <>
          <Card>
            <StatRow>
              <Stat label="Available now" value={remainingCredits} isLoading={isLoading} />
              <Stat
                label={reset ? "Used this period" : "Used so far"}
                value={usedCredits}
                isLoading={isLoading}
              />
              <Stat label="Allowance" value={totalCredits} isLoading={isLoading} />
            </StatRow>
            <CreditUsageBar
              totalCredits={totalCredits}
              usedCredits={usedCredits}
              remainingCredits={remainingCredits}
              marginalCredits={0}
              resetDate={reset?.date}
            />
            {resetMessage && <Description>{resetMessage}</Description>}
          </Card>

          <Card>
            <Flex column gap={4}>
              <Text fontWeight="600">Usage over the last 12 months</Text>
              {/* Only claim a figure once we have one — `totalUsed` reads 0 while loading, which
                  would flash "0 credits spent" at a company that has spent plenty. */}
              <Description>{usageSummary(history)}</Description>
            </Flex>

            {!history.isLoading && !history.isError && (
              <ContactCreditsUsageChart months={history.months} />
            )}
          </Card>

          {/* Admin-only: naming who spent what exposes colleagues' names and emails. The card is
              mounted conditionally so a non-admin never fires the (admin-gated) query at all. */}
          {isCompanyAdmin && (
            <Card>
              <Flex column gap={4}>
                <Text fontWeight="600">Spend by user</Text>
                <Description>Credits each person spent over the last 12 months.</Description>
              </Flex>
              <ContactCreditsSpendByUser />
            </Card>
          )}
        </>
      )}
    </Flex>
  );
}

function resetCopy(reset?: ContactsCreditsReset): string | null {
  if (!reset) return null;
  return `Your allowance resets ${CADENCE_ADVERB[reset.cadence]}, next on ${reset.date}. Unused credits don't carry over.`;
}

function usageSummary(history: ContactsCreditUsageHistory): string {
  if (history.isError) return "We couldn't load your usage history.";
  if (history.isLoading) return "Loading your usage…";
  return `${pluralise(history.totalUsed, "credit", true)} spent in the last 12 months.`;
}

type StatProps = {
  label: string;
  value: number;
  isLoading: boolean;
};

function Stat({ label, value, isLoading }: StatProps): JSX.Element {
  return (
    <Flex column gap={2}>
      {isLoading ? (
        <SkeletonText width="72px" fontSize={24} />
      ) : (
        <Text h2>{numberFormatLong(value)}</Text>
      )}
      <Text description color="sysTextSecondary">
        {label}
      </Text>
    </Flex>
  );
}

const Description = styled(Text)(({ theme }) => ({
  color: theme.colors.sysTextSecondary,
  maxWidth: 650,
}));

const Card = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  gap: 16,
  padding: 24,
  maxWidth: 780,
  borderRadius: 8,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  backgroundColor: theme.colors.sysBackgroundDefault,
}));

const StatRow = styled(Flex)({
  flexWrap: "wrap",
  gap: 48,
});
